import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { AuthorizationService } from 'app/core/services/authorization.service';

/**
 * Enforces `data.anyRoles` / `data.roles` / permission keys declared on routes.
 *
 * Design contract (see app.routes.ts):
 * - Parent admin shell only requires AuthGuard (any authenticated non-anonymous user).
 * - Child modules declare `data: { anyRoles: [...] }` for role-scoped surfaces.
 * - Without this guard those declarations are documentation-only and UI-only.
 *
 * Fail-closed: missing access → `/forbidden` (kanban #63: an in-shell page —
 * this guard only ever runs post-AuthGuard, inside the shell, so the target
 * must stay inside it too instead of dropping the user onto the bare,
 * shell-less `/errors/forbidden` page).
 */
export const RouteDataAuthorizationGuard: CanActivateChildFn = (childRoute) => {
    const authorization = inject(AuthorizationService);
    const router = inject(Router);

    // Walk from the leaf all the way to the router root so nested modules still
    // honor an ancestor's data.anyRoles. `canAccessRoute` is a no-op (`true`) on
    // any node with no `data`, so walking past the meaningful shell costs a few
    // idle iterations — cheaper than the false-negative of stopping short. The
    // previous "stop at the shell root" heuristic (path === '' with a
    // primary-outlet parent) matched *every* empty-path wrapper route, not just
    // the true root, so it broke out one hop before reaching an ancestor like
    // features/photoqa/photoqa.routes.ts's guarded group — silently skipping
    // its `data.anyRoles` check and letting any authenticated role reach
    // /photoqa's children with no /errors/forbidden redirect (#229).
    let route = childRoute;
    while (route) {
        if (!authorization.canAccessRoute(route.routeConfig)) {
            return router.createUrlTree(['/forbidden']);
        }
        route = route.parent as typeof childRoute;
    }

    return true;
};
